<template>
  <component :is="layoutComponent">
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1">
            Fiche client
            <span v-if="client" class="text-muted">#{{ client.name }}</span>
          </h1>
          <div v-if="client" class="small text-muted">
            Créé le {{ $format.datetime(client.created_at) }}
          </div>
        </div>

        <div class="d-flex gap-2">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            title="Retour à la liste"
            @click="$router.push({ name: 'Clients' })"
          >
            <i class="fa fa-arrow-left"></i>
            <span class="btn-label ms-1">Retour à la liste</span>
          </button>
          <button
            v-if="canManageClients"
            type="button"
            class="btn btn-outline-primary btn-sm btn-icon-label"
            :disabled="!client"
            title="Modifier"
            @click="openEditDialog"
          >
            <i class="fa fa-pen"></i>
            <span class="btn-label ms-1">Modifier</span>
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2">
        {{ errorMessage }}
      </div>
      <div v-if="successMessage" class="alert alert-success py-2">
        {{ successMessage }}
      </div>

      <div v-if="isLoading" class="text-center my-5">
        <i class="fa fa-spinner fa-spin fa-2x mb-2"></i>
        <div class="small text-muted">Chargement du client...</div>
      </div>

      <div v-else-if="!client && !errorMessage" class="alert alert-warning">
        Client introuvable.
      </div>

      <div v-else-if="client" class="row g-3 align-items-start">

        <!-- Infos client -->
        <div class="col-lg-4">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-0 pb-1">
              <h6 class="mb-0">Informations client</h6>
            </div>
            <div class="card-body pt-2 pb-3">
              <div class="mb-2">
                <div class="small text-muted">Nom</div>
                <div class="fw-semibold">{{ client.name }}</div>
              </div>

              <div v-if="client.phone" class="mb-2">
                <div class="small text-muted">Téléphone</div>
                <div>{{ client.phone }}</div>
              </div>

              <div v-if="client.whatsapp_phone" class="mb-2">
                <div class="small text-muted">WhatsApp</div>
                <div>{{ client.whatsapp_phone }}</div>
              </div>

              <div v-if="client.email" class="mb-2">
                <div class="small text-muted">Email</div>
                <div>{{ client.email }}</div>
              </div>

              <div v-if="client.city || client.address" class="mb-2">
                <div class="small text-muted">Adresse</div>
                <div>
                  <span v-if="client.address">{{ client.address }}</span>
                  <span v-if="client.city">, {{ client.city }}</span>
                </div>
              </div>

              <div v-if="client.notes" class="mt-2">
                <div class="small text-muted">Notes</div>
                <div>{{ client.notes }}</div>
              </div>

              <hr />

              <div class="mb-1 d-flex justify-content-between">
                <span class="small text-muted">Nombre de ventes</span>
                <span class="fw-semibold">{{ sales.length }}</span>
              </div>
              <div class="mb-1 d-flex justify-content-between">
                <span class="small text-muted">Montant total ventes</span>
                <span class="fw-bold">
                  {{ $format.currency(totalSalesAmount) }}
                </span>
              </div>
              <hr />
              <div class="mb-1 d-flex justify-content-between">
                <span class="small text-muted">Plafond dette</span>
                <span class="fw-semibold">{{ $format.currency(client.debt_limit || 0) }}</span>
              </div>
              <div class="mb-1 d-flex justify-content-between">
                <span class="small text-muted">Dette actuelle</span>
                <span class="fw-bold" :class="Number(client.current_debt || 0) > 0 ? 'text-danger' : ''">
                  {{ $format.currency(client.current_debt || 0) }}
                </span>
              </div>
              <div class="mb-1 d-flex justify-content-between">
                <span class="small text-muted">Crédit disponible</span>
                <span class="fw-bold" :class="Number(client.available_credit || 0) <= 0 ? 'text-warning' : 'text-success'">
                  {{ $format.currency(client.available_credit || 0) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Historique ventes -->
        <div class="col-lg-8">
          <div class="client-sales-heading d-flex justify-content-between align-items-center mb-2">
            <h6 class="mb-0">Lignes de vente du client</h6>
            <small class="text-muted" v-if="sales.length">
              {{ sales.length }} vente(s)
            </small>
          </div>

          <BootstrapDataTable
            :rows="sales"
            :columns="saleColumns"
            :loading="isLoadingSaleRows"
            empty-text="Aucune vente enregistrée pour ce client."
            @row-click="openSaleDetailDialog"
            @refresh="fetchClient"
          >
            <template #cell-reference="{ row }">
              <span class="fw-semibold">{{ row.reference }}</span>
            </template>

            <template #cell-status="{ row }">
              <span class="badge" :class="saleStatusClass(row.status)">
                {{ saleStatusLabel(row.status) }}
              </span>
            </template>

            <template #cell-total_amount="{ row }">
              {{ $format.currency(row.total_amount || 0) }}
            </template>

            <template #cell-credit_due_amount="{ row }">
              <span v-if="row.payment_method === 'credit'" :class="Number(row.credit_due_amount || 0) > 0 ? 'text-danger fw-semibold' : 'text-success fw-semibold'">
                {{ $format.currency(row.credit_due_amount || 0) }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>

            <template #cell-created_at="{ row }">
              {{ $format.date(row.created_at) }}
            </template>
          </BootstrapDataTable>
        </div>

        <div class="col-12">
          <div class="client-sales-heading d-flex justify-content-between align-items-center mb-2">
            <h6 class="mb-0">Règlements client</h6>
            <button
              v-if="canPayClientDebt"
              type="button"
              class="btn btn-primary btn-sm btn-icon-label"
              :disabled="Number(client.current_debt || 0) <= 0"
              @click="openPaymentDialog"
            >
              <i class="fa fa-plus"></i>
              <span class="btn-label ms-1">Ajouter règlement</span>
            </button>
          </div>

          <BootstrapDataTable
            :rows="payments"
            :columns="paymentColumns"
            :loading="isLoadingPayments"
            empty-text="Aucun règlement enregistré pour ce client."
            @refresh="fetchPayments"
          >
            <template #cell-amount="{ row }">
              {{ $format.currency(row.amount || 0) }}
            </template>

            <template #cell-payment_date="{ row }">
              {{ $format.date(row.payment_date) }}
            </template>

            <template #cell-method="{ row }">
              {{ paymentMethodLabel(row.method) }}
            </template>

            <template #cell-sale="{ row }">
              {{ row.sale?.reference || 'Compte client' }}
            </template>

            <template #cell-actions="{ row }">
              <AppLoadingButton
                v-if="canPayClientDebt"
                type="button"
                class="btn btn-outline-danger btn-sm"
                :loading="isDeletingPayment(row)"
                loading-label=""
                title="Supprimer le règlement"
                @click.stop="deletePayment(row)"
              >
                <i class="fa fa-trash"></i>
              </AppLoadingButton>
            </template>
          </BootstrapDataTable>
        </div>
      </div>

      <PrimevueDialog
        v-model:visible="formDialogVisible"
        :modal="true"
        :style="{ width: '600px' }"
        :closable="false"
      >
        <template #header>
          <h5 class="mb-0">Modifier le client</h5>
        </template>

        <ClientForm
          v-if="client"
          :mode="'edit'"
          :client="client"
          :submitting="isSavingForm"
          @save="handleSave"
          @cancel="closeFormDialog"
        />
      </PrimevueDialog>

      <PrimevueDialog
        v-model:visible="paymentDialogVisible"
        :modal="true"
        :style="{ width: '520px', maxWidth: '96vw' }"
        :closable="false"
      >
        <template #header>
          <h5 class="mb-0">Ajouter règlement client</h5>
        </template>

        <div v-if="paymentError" class="alert alert-danger py-2">
          {{ paymentError }}
        </div>

        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label small fw-semibold">Montant</label>
            <PrimevueInputNumber
              v-model="paymentForm.amount"
              class="w-100"
              mode="decimal"
              :min="0"
              :maxFractionDigits="2"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label small fw-semibold">Date</label>
            <input v-model="paymentForm.payment_date" type="date" class="form-control" />
          </div>
          <div class="col-md-6">
            <label class="form-label small fw-semibold">Méthode</label>
            <PrimevueDropdown
              v-model="paymentForm.method"
              :options="paymentMethodOptions"
              optionLabel="label"
              optionValue="value"
              class="w-100"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label small fw-semibold">Vente à régler</label>
            <PrimevueDropdown
              v-model="paymentForm.sale_id"
              :options="creditSaleOptions"
              optionLabel="label"
              optionValue="value"
              class="w-100"
            />
          </div>
          <div class="col-12">
            <label class="form-label small fw-semibold">Référence</label>
            <PrimevueInputText v-model="paymentForm.reference" class="w-100" />
          </div>
          <div class="col-12">
            <label class="form-label small fw-semibold">Notes</label>
            <PrimevueTextarea v-model="paymentForm.notes" class="w-100" rows="2" autoResize />
          </div>
        </div>

        <template #footer>
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isSavingPayment" @click="closePaymentDialog">
            <i class="fa fa-xmark me-1" aria-hidden="true"></i>
            Annuler
          </button>
          <AppLoadingButton
            type="button"
            class="btn btn-primary btn-sm"
            :loading="isSavingPayment"
            loading-label="Enregistrement..."
            @click="submitPayment"
          >
            <i class="fa fa-save me-1"></i>
            Enregistrer
          </AppLoadingButton>
        </template>
      </PrimevueDialog>

      <PrimevueDialog
        v-model:visible="saleDetailDialogVisible"
        :modal="true"
        :style="{ width: '900px', maxWidth: '96vw' }"
        :closable="false"
      >
        <template #header>
          <div>
            <h5 class="mb-1">Détail de la vente</h5>
            <div v-if="selectedSaleSummary" class="small text-muted">
              {{ selectedSaleSummary.reference }}
            </div>
          </div>
        </template>

        <div v-if="isLoadingSaleDetail" class="text-center py-4">
          <i class="fa fa-spinner fa-spin me-1"></i>
          Chargement de la vente...
        </div>

        <div v-else>
          <div v-if="saleDetailError" class="alert alert-danger py-2">
            {{ saleDetailError }}
          </div>

          <div v-if="selectedSaleDetail">
            <div class="sale-detail-summary mb-3">
              <div>
                <span class="small text-muted d-block">Référence</span>
                <strong>{{ selectedSaleDetail.reference }}</strong>
              </div>
              <div>
                <span class="small text-muted d-block">Statut</span>
                <span class="badge" :class="saleStatusClass(selectedSaleDetail.status)">
                  {{ saleStatusLabel(selectedSaleDetail.status) }}
                </span>
              </div>
              <div>
                <span class="small text-muted d-block">Date</span>
                <strong>{{ $format.date(selectedSaleDetail.created_at) }}</strong>
              </div>
              <div>
                <span class="small text-muted d-block">Total</span>
                <strong>{{ $format.currency(selectedSaleDetail.total_amount || 0) }}</strong>
              </div>
            </div>

            <BootstrapDataTable
              :rows="saleDetailRows"
              :columns="saleLineColumns"
              row-key="row_key"
              empty-text="Aucune ligne sur cette vente."
              @refresh="fetchClient"
            >
              <template #cell-unit_price="{ row }">
                {{ $format.currency(row.unit_price || 0) }}
              </template>

              <template #cell-total_line="{ row }">
                {{ $format.currency(row.total_line || 0) }}
              </template>
            </BootstrapDataTable>
          </div>
        </div>

        <template #footer>
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            @click="closeSaleDetailDialog"
          >
            <i class="fa fa-xmark me-1" aria-hidden="true"></i>
            Fermer
          </button>
          <button
            v-if="canOpenSalePage"
            type="button"
            class="btn btn-primary btn-sm btn-icon-label"
            :disabled="!selectedSaleSummary"
            @click="goToSale(selectedSaleSummary)"
          >
            <i class="fa fa-arrow-up-right-from-square"></i>
            <span class="btn-label ms-1">Ouvrir la page vente</span>
          </button>
        </template>
      </PrimevueDialog>
    </template>
  </component>
</template>

<script>
import ClientForm from './ClientForm.vue';
import { extractApiErrorMessage } from '@/support/apiErrors';
import { canBypassPermissions } from '@/support/permissions';

export default {
  name: 'ClientShowView',
  components: { ClientForm },
  data() {
    return {
      client: null,
      sales: [],
      payments: [],
      isLoading: false,
      isLoadingPayments: false,
      errorMessage: '',
      successMessage: '',
      formDialogVisible: false,
      isSavingForm: false,
      paymentDialogVisible: false,
      isSavingPayment: false,
      deletingPaymentId: null,
      paymentError: '',
      paymentForm: {
        amount: null,
        payment_date: '',
        method: 'cash',
        sale_id: null,
        reference: '',
        notes: '',
      },
      paymentMethodOptions: [
        { label: 'Espèces', value: 'cash' },
        { label: 'Mobile money', value: 'mobile_money' },
        { label: 'Carte bancaire', value: 'card' },
        { label: 'Virement', value: 'bank_transfer' },
        { label: 'Autre', value: 'other' },
      ],
      saleDetailDialogVisible: false,
      selectedSaleSummary: null,
      selectedSaleDetail: null,
      isLoadingSaleDetail: false,
      saleDetailError: '',
      isLoadingSaleRows: false,
      saleColumns: [
        { key: 'reference', label: 'Référence' },
        { key: 'status', label: 'Statut', class: 'text-center' },
        { key: 'total_amount', label: 'Montant', class: 'text-end' },
        { key: 'credit_due_amount', label: 'Reste crédit', class: 'text-end' },
        { key: 'created_at', label: 'Date' },
      ],
      paymentColumns: [
        { key: 'payment_date', label: 'Date' },
        { key: 'amount', label: 'Montant', class: 'text-end', headerClass: 'text-end' },
        { key: 'method', label: 'Méthode' },
        { key: 'reference', label: 'Référence' },
        { key: 'sale', label: 'Vente' },
        { key: 'actions', label: '', class: 'text-end', headerClass: 'text-end', style: 'width: 80px;' },
      ],
      saleLineColumns: [
        { key: 'line_number', label: '#', class: 'text-center', style: 'width: 64px;' },
        { key: 'product_name', label: 'Produit' },
        { key: 'unit_price', label: 'PU', class: 'text-end' },
        { key: 'quantity', label: 'Qté', class: 'text-center' },
        { key: 'total_line', label: 'Total ligne', class: 'text-end' },
      ],
    };
  },

  computed: {
    currentUser() {
      return this.$store.getters['auth/currentUser'];
    },

    isCashier() {
      return this.currentUser?.role === 'caissier';
    },

    canManageClients() {
      return canBypassPermissions(this.currentUser);
    },

    canPayClientDebt() {
      return ['gerant', 'caissier'].includes(this.currentUser?.role);
    },

    canOpenSalePage() {
      return this.currentUser?.role === 'gerant';
    },

    layoutComponent() {
      return this.isCashier ? 'CashierLayout' : 'AdminLayout';
    },

    totalSalesAmount() {
      return this.sales.reduce((sum, s) => sum + Number(s.total_amount || 0), 0);
    },

    creditSaleOptions() {
      return [
        { label: 'Dette globale du client', value: null },
        ...this.sales
          .filter((sale) => sale.payment_method === 'credit' && Number(sale.credit_due_amount || 0) > 0)
          .map((sale) => ({
            label: `${sale.reference} · reste ${this.$format.currency(sale.credit_due_amount || 0)}`,
            value: sale.id,
          })),
      ];
    },

    saleDetailRows() {
      const items = this.selectedSaleDetail?.items || [];

      return items.map((item, index) => ({
        ...item,
        row_key: item.id || `${item.product_id || 'product'}-${index}`,
        line_number: index + 1,
      }));
    },
  },

  mounted() {
    this.fetchClient();
  },

  methods: {
    routeClientId() {
      return this.$route?.params?.id || null;
    },

    async fetchClient() {
      const id = this.routeClientId();

      if (!id) {
        this.client = null;
        this.sales = [];
        this.payments = [];
        this.errorMessage = 'Client introuvable.';
        return;
      }

      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';
      this.client = null;
      this.sales = [];

      try {
        const res = await this.$api.get(`/clients/${id}`);
        const data = res.data.data;
        this.client = data;
        this.sales = data.sales || [];
        await this.fetchPayments();
      } catch (error) {
        this.errorMessage = extractApiErrorMessage(error, 'Impossible de charger ce client.');
      } finally {
        this.isLoading = false;
      }
    },

    async fetchPayments() {
      const id = this.routeClientId();

      if (!id) {
        this.payments = [];
        return;
      }

      this.isLoadingPayments = true;

      try {
        const res = await this.$api.get(`/clients/${id}/payments`);
        this.payments = res.data.data || [];
      } catch (error) {
        this.payments = [];
      } finally {
        this.isLoadingPayments = false;
      }
    },

    todayDate() {
      return new Date().toISOString().slice(0, 10);
    },

    openPaymentDialog() {
      if (!this.client) return;

      this.paymentError = '';
      this.paymentForm = {
        amount: Number(this.client.current_debt || 0),
        payment_date: this.todayDate(),
        method: 'cash',
        sale_id: null,
        reference: '',
        notes: '',
      };
      this.paymentDialogVisible = true;
    },

    closePaymentDialog() {
      this.paymentDialogVisible = false;
      this.paymentError = '';
      this.isSavingPayment = false;
    },

    async submitPayment() {
      if (!this.client || this.isSavingPayment) return;

      const amount = Number(this.paymentForm.amount || 0);

      if (amount <= 0) {
        this.paymentError = 'Le montant du règlement doit être supérieur à zéro.';
        return;
      }

      this.isSavingPayment = true;
      this.paymentError = '';

      try {
        await this.$api.post(`/clients/${this.client.id}/payments`, {
          amount,
          payment_date: this.paymentForm.payment_date,
          method: this.paymentForm.method,
          sale_id: this.paymentForm.sale_id,
          reference: this.paymentForm.reference,
          notes: this.paymentForm.notes,
        });

        this.paymentDialogVisible = false;
        await this.fetchClient();
        this.successMessage = 'Règlement client enregistré avec succès.';
      } catch (error) {
        this.paymentError =
          error.response?.data?.errors
            ? Object.values(error.response.data.errors).flat().join(' ')
            : 'Impossible d’enregistrer ce règlement.';
      } finally {
        this.isSavingPayment = false;
      }
    },

    async deletePayment(payment) {
      if (!payment?.id || !this.canPayClientDebt) return;
      if (this.isDeletingPayment(payment)) return;

      this.deletingPaymentId = payment.id;

      const ok = await this.$confirmAction('Supprimer ce règlement client ? La dette sera recalculée.');
      if (!ok) {
        this.deletingPaymentId = null;
        return;
      }

      try {
        await this.$api.delete(`/client-payments/${payment.id}`);
        await this.fetchClient();
        this.successMessage = 'Règlement client supprimé.';
      } catch (error) {
        this.errorMessage =
          error.response?.data?.errors
            ? Object.values(error.response.data.errors).flat().join(' ')
            : 'Impossible de supprimer ce règlement.';
      } finally {
        this.deletingPaymentId = null;
      }
    },

    isDeletingPayment(payment) {
      return Number(this.deletingPaymentId || 0) === Number(payment?.id || 0);
    },

    paymentMethodLabel(method) {
      if (method === 'cash') return 'Espèces';
      if (method === 'mobile_money') return 'Mobile money';
      if (method === 'card') return 'Carte bancaire';
      if (method === 'bank_transfer') return 'Virement';
      if (method === 'credit') return 'Crédit';
      if (method === 'other') return 'Autre';
      return method || '-';
    },

    saleStatusLabel(status) {
      if (status === 'validated') return 'Validée';
      if (status === 'draft') return 'Brouillon';
      if (status === 'cancelled') return 'Annulée';
      if (status === 'abandoned') return 'Abandonnée';
      return status;
    },

    saleStatusClass(status) {
      if (status === 'validated') return 'bg-success';
      if (status === 'draft') return 'bg-secondary';
      if (status === 'cancelled') return 'bg-danger';
      if (status === 'abandoned') return 'bg-secondary';
      return 'bg-light text-dark';
    },

    goToSale(sale) {
      if (!this.canOpenSalePage) return;
      if (!sale) return;

      this.$router.push({ name: 'SaleShow', params: { id: sale.id } });
    },

    async openSaleDetailDialog(sale) {
      if (!sale || !sale.id) return;

      this.selectedSaleSummary = sale;
      this.selectedSaleDetail = null;
      this.saleDetailError = '';
      this.saleDetailDialogVisible = true;
      this.isLoadingSaleDetail = true;

      try {
        const res = await this.$api.get(`/sales/${sale.id}`);
        this.selectedSaleDetail = res.data.data;
      } catch (error) {
        this.saleDetailError = 'Impossible de charger le détail de cette vente.';
      } finally {
        this.isLoadingSaleDetail = false;
      }
    },

    closeSaleDetailDialog() {
      this.saleDetailDialogVisible = false;
      this.selectedSaleSummary = null;
      this.selectedSaleDetail = null;
      this.saleDetailError = '';
      this.isLoadingSaleDetail = false;
    },

    openEditDialog() {
      if (!this.canManageClients) return;
      if (!this.client) return;

      this.formDialogVisible = true;
    },

    closeFormDialog() {
      this.formDialogVisible = false;
    },

    async handleSave(payload) {
      if (!this.canManageClients) return;
      if (!this.client) return;
      if (this.isSavingForm) return;

      this.errorMessage = '';
      this.successMessage = '';
      this.isSavingForm = true;

      try {
        await this.$api.put(`/clients/${this.client.id}`, payload);
        this.formDialogVisible = false;
        await this.fetchClient();
        this.successMessage = 'Client mis à jour avec succès.';
      } catch (error) {
        const msg =
          error.response?.data?.errors
            ? Object.values(error.response.data.errors).flat().join(' ')
            : 'Une erreur est survenue lors de la mise à jour du client.';

        this.errorMessage = msg;
      } finally {
        this.isSavingForm = false;
      }
    },
  },
};
</script>

<style scoped>
.card {
  border-radius: 0.75rem;
}

.client-sales-heading {
  min-height: 32px;
}

.sale-detail-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
  padding: 0.875rem 1rem;
  border: 1px solid rgba(33, 37, 41, 0.08);
  border-radius: 8px;
  background: #f8fafc;
}

@media (max-width: 767.98px) {
  .sale-detail-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
